/**
 * @description
 * UseAvoirDialogComponent provides a modal dialog for selecting an available credit note (`AVOIR`)
 * — either by scanning its QR code, typing its reference (`AV-YYYY-SEQ`), or picking from the customer's
 * open credit notes — and imputing it (partially or in full) onto a sales order or counter ticket.
 *
 * Called via: MatDialog.open(UseAvoirDialogComponent, ...)
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Inject, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { catchError, finalize, of } from 'rxjs';

import { AlertService } from '@app/shared/alert.service';
import { FormatCurrencyPipe } from '@app/shared/pipes/format-currency.pipe';
import { AvailableAvoirDto, QuotationService } from '../quotation.service';

export interface UseAvoirDialogData {
  orderId: number;
  customerId: number;
  customerName?: string;
  remainder: number;
  initialAmount?: number;
  initialComment?: string;
}

@Component({
  selector: 'app-use-avoir-dialog',
  standalone: true,
  templateUrl: './use-avoir-dialog.component.html',
  styleUrls: ['./use-avoir-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    FormatCurrencyPipe,
  ],
})
export class UseAvoirDialogComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private quotationService = inject(QuotationService);
  private alertService = inject(AlertService);

  public isLoading = false;
  public isSubmitting = false;
  public availableAvoirs: AvailableAvoirDto[] = [];
  public selectedAvoir: AvailableAvoirDto | null = null;
  public searchError: string | null = null;

  public searchControl = new FormControl<string>('');

  public formGroup = new FormGroup({
    amount: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
    comment: new FormControl<string>(''),
  });

  constructor(
    public dialogRef: MatDialogRef<UseAvoirDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UseAvoirDialogData,
  ) {}

  ngOnInit(): void {
    if (this.data.initialComment) {
      this.formGroup.get('comment')?.setValue(this.data.initialComment);
    }
    this.loadAvoirs();
  }

  /**
   * Loads available credit notes from backend.
   * When `docRef` is provided, searches across all credit notes (including counter customer = 0).
   * Otherwise loads open credit notes for `data.customerId` (if > 0) or recent open credit notes.
   */
  public loadAvoirs(docRef?: string): void {
    this.isLoading = true;
    this.searchError = null;
    this.cdr.markForCheck();

    const trimmedRef = docRef?.trim() || '';
    this.quotationService
      .getAvailableAvoirs({
        customerId: trimmedRef ? undefined : this.data.customerId,
        docRef: trimmedRef || undefined,
        limit: 50,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(err => {
          this.searchError = err?.error?.error || err?.message || 'Erreur lors de la recherche des avoirs disponibles.';
          return of([] as AvailableAvoirDto[]);
        }),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe(list => {
        this.availableAvoirs = (list || []).map(item => ({
          ...item,
          initial_amount: Number(item.initial_amount) || 0,
          consumed_amount: Number(item.consumed_amount) || 0,
          available_amount: Number(item.available_amount) || 0,
        }));

        if (trimmedRef && this.availableAvoirs.length === 0) {
          this.searchError = `Aucun avoir disponible trouvé pour « ${trimmedRef} ».`;
          this.selectedAvoir = null;
        } else if (this.availableAvoirs.length === 1) {
          this.selectAvoir(this.availableAvoirs[0]);
        } else if (this.selectedAvoir) {
          const stillPresent = this.availableAvoirs.find(a => a.avoir_order_id === this.selectedAvoir?.avoir_order_id);
          if (stillPresent) {
            this.selectAvoir(stillPresent);
          } else {
            this.selectedAvoir = null;
          }
        }
        this.cdr.markForCheck();
      });
  }

  public onSearch(): void {
    this.loadAvoirs(this.searchControl.value || '');
  }

  public onClearSearch(): void {
    this.searchControl.setValue('');
    this.loadAvoirs();
  }

  public onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.onSearch();
    }
  }

  public selectAvoir(avoir: AvailableAvoirDto): void {
    this.selectedAvoir = avoir;
    const maxAmount = this.maxImputableAmount;
    const requestedInitial =
      this.data.initialAmount && this.data.initialAmount > 0
        ? Math.min(this.data.initialAmount, maxAmount)
        : maxAmount;

    const amountCtrl = this.formGroup.get('amount');
    amountCtrl?.setValidators([Validators.required, Validators.min(0.01), Validators.max(maxAmount)]);
    amountCtrl?.setValue(Math.round(requestedInitial * 100) / 100);
    amountCtrl?.updateValueAndValidity();
    this.cdr.markForCheck();
  }

  public get maxImputableAmount(): number {
    if (!this.selectedAvoir) return 0;
    const rem = Math.max(0, Number(this.data.remainder) || 0);
    const avail = Math.max(0, Number(this.selectedAvoir.available_amount) || 0);
    return Math.round(Math.min(rem, avail) * 100) / 100;
  }

  public get currentAmount(): number {
    return Math.max(0, Number(this.formGroup.get('amount')?.value) || 0);
  }

  public get remainingOnAvoirAfter(): number {
    if (!this.selectedAvoir) return 0;
    return Math.max(0, Math.round((this.selectedAvoir.available_amount - this.currentAmount) * 100) / 100);
  }

  public get remainingOnOrderAfter(): number {
    return Math.max(0, Math.round((this.data.remainder - this.currentAmount) * 100) / 100);
  }

  public setFullMaxAmount(): void {
    if (!this.selectedAvoir) return;
    this.formGroup.get('amount')?.setValue(this.maxImputableAmount);
    this.cdr.markForCheck();
  }

  public onSubmit(): void {
    if (this.isSubmitting || !this.selectedAvoir || this.formGroup.invalid) {
      return;
    }

    const amount = Number(this.formGroup.get('amount')?.value);
    if (!amount || amount <= 0 || amount > this.maxImputableAmount) {
      this.alertService.error(`Le montant à imputer doit être compris entre 0,01 € et ${this.maxImputableAmount.toFixed(2)} €.`);
      return;
    }

    this.isSubmitting = true;
    this.cdr.markForCheck();

    const commentVal = (this.formGroup.get('comment')?.value || '').trim();
    this.quotationService
      .applyAvoir(this.data.orderId, {
        avoir_order_id: this.selectedAvoir.avoir_order_id,
        avoir_doc_ref: this.selectedAvoir.doc_ref,
        amount,
        comment: commentVal || undefined,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isSubmitting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: resp => {
          if (resp && resp.success === false) {
            this.alertService.error(resp.error || "Échec de l'imputation de l'avoir.");
            return;
          }
          this.alertService.success(`Avoir ${this.selectedAvoir?.doc_ref} imputé à hauteur de ${amount.toFixed(2)} €.`);
          this.dialogRef.close(resp);
        },
        error: err => {
          const msg = err?.error?.error || err?.error?.msg || err?.message || "Erreur lors de l'imputation de l'avoir.";
          this.alertService.error(msg);
        },
      });
  }

  public onCancel(): void {
    this.dialogRef.close(null);
  }
}
